import { Component, Inject, PLATFORM_ID, inject } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { MatButtonModule } from '@angular/material/button';
import { CommonModule, DOCUMENT } from '@angular/common';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { environment } from '../environments/environment';
import { getCookie } from './utils/getCookies';
import { DEFAULT_LANGUAGE, detectBrowserLanguage, getConectioSiteLanguage, getLastLanguage, getSessionLanguage, getStoredLanguage, isSupportedLanguage, storeLastLanguage, storeSessionLanguage } from './utils/language';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, TranslateModule, MatButtonModule, CommonModule  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent {
  title = 'conectio-client';


  constructor(private translate: TranslateService, @Inject(PLATFORM_ID) private platformId: Object, private http: HttpClient, @Inject(DOCUMENT) private document: Document) {
    // Spanish is the most complete translation: load it as fallback for keys missing in other languages
    translate.setFallbackLang(DEFAULT_LANGUAGE);

    // Keep <html lang> in sync with the active language (accessibility and SEO).
    // In a tab that came from conectio.io/<lang>/, later changes (e.g. the flags) update the language kept for it.
    translate.onLangChange.subscribe(({ lang }) => {
      this.document.documentElement.lang = lang;
      if (isPlatformBrowser(this.platformId)) {
        storeLastLanguage(lang);
        if (getSessionLanguage()) {
          storeSessionLanguage(lang);
        }
      }
    });

    // Server-side rendering has no browser language or storage: render in Spanish
    if (!isPlatformBrowser(this.platformId)) {
      translate.use(DEFAULT_LANGUAGE);
    }

    // Language: the user's saved choice, else the company account setting, else the browser language.
    // Exception: arriving from conectio.io/<lang>/ always shows that language (not saved as a choice).
    // That language is kept for the rest of the visit in this tab, so the sign-up flow stays in it after
    // LinkedIn's redirect (which loses the referrer). Instead of the browser language, the tab keeps the last
    // language it showed (e.g. back from LinkedIn, when the company language request fails for a candidate).
    if (isPlatformBrowser(this.platformId)) {
      const siteLanguage = getConectioSiteLanguage(this.document.referrer, this.document.location.search);
      const sessionLanguage = getSessionLanguage();
      const storedLanguage = getStoredLanguage();

      if (siteLanguage) {
        storeSessionLanguage(siteLanguage);
        translate.use(siteLanguage);
      } else if (sessionLanguage) {
        translate.use(sessionLanguage);
      } else if (storedLanguage) {
        translate.use(storedLanguage);
      } else if (getCookie('access_token')) {
        this.http.get(`${environment.url}/api/v1/company/get_language`, {
          headers: {
            'Authorization': `Bearer ${getCookie('access_token')}`
          }
        }).subscribe({
          next: (data: any) => translate.use(isSupportedLanguage(data?.language) ? data.language : getLastLanguage() ?? detectBrowserLanguage()),
          error: () => translate.use(getLastLanguage() ?? detectBrowserLanguage())
        });
      } else {
        translate.use(getLastLanguage() ?? detectBrowserLanguage());
      }
    }
  }

  throwTestError() {
    throw new Error('Test Sentry error');
  }
}
